import { getNewCartList, getSubTotalPrice } from '@/api/cart';
import { ICartGroup, ITotalData } from '@/api/types/cart';
import { fetchCartAutoOfferAbValue, normalizeAppliedOffer } from '@/domain/cart/auto-offer';
import { ProviderType, ReportingCenter } from '@/reporting';
import { EmarsysProvider } from '@/reporting/providers/emarsysProvider';
import { Store } from '@/store';
import logger from '@/utils/client-logger';
import { IS_US, MOBILE_SCREEN_WIDTH } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { genItemPrice } from '@/utils/price';
import { setLocal } from '@/utils/tools';
import { StateCreator } from 'zustand';

// Cookie获取工具函数
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

// 这些接口移到了对应的组件中使用，不再需要全局定义

export interface CartState {
  /** 当前身份下的购物车列表是否已完成一次写入 */
  cartDataReady: boolean;
  // 可结算购物车列表 。 第一购物车列表 表示第一购物车（Out of Stock & Invalid Items 上面的购物车列表）渲染的所有品。 跟接口的 list 字段对应
  canBuyList: ICartGroup[];
  // 可结算购物车条目列表（可勾选的）。 所有可勾选的购物车列表。 跟接口的 can_buy_list 字段对应
  canBuyCartsList: ICartGroup[];
  // 待配镜购物车列表。只用于 Frame List 展示，不参与勾选、计价和结算
  pendingLensList: ICartGroup[];
  // 不可结算购物车列表 。 表示第二购物车（Out of Stock & Invalid Items 下面的购物车列表）渲染的所有品. 跟接口的 can_not_sell_list 字段对应
  canNotBuyList: ICartGroup[];
  // 选中的购物车商品ID列表
  selectedCartIds: number[];
  // 购物车总价信息
  subTotal: ITotalData;
  // 这些配置现在直接从 publicResourceData 获取，不再需要单独的 state
  // 赠品相关数据
  giftData: {
    should_pop: boolean;
    is_pick_up: boolean;
    cart: any;
  };
  giftCount: number;
  uuidOrderList: number[];
}

export interface CartActions {
  // 设置购物车数据
  setCanBuyList: (canBuyList: CartState['canBuyList']) => void;
  setCanBuyCartsList: (canBuyCartsList: CartState['canBuyCartsList']) => void;
  setPendingLensList: (pendingLensList: CartState['pendingLensList']) => void;
  setCanNotBuyList: (canNotBuyList: CartState['canNotBuyList']) => void;
  setSelectedCartIds: (selectedCartIds: CartState['selectedCartIds']) => void;
  setSubTotal: (subTotal: Omit<ITotalData, 'applied_offer'> & { applied_offer?: any }) => void;
  setGiftData: (giftData: CartState['giftData']) => void;
  setGiftCount: (giftCount: CartState['giftCount']) => void;

  // 配置相关方法已移除，直接从 publicResourceData 获取

  // Vue项目对应的核心方法
  setCart: (cartdata?: any) => Promise<any>;
  cartTotalPrice: () => Promise<void>;
  setUuidOrderList: (uuidOrderList: number[]) => void;
}

export type CartSlice = CartState & CartActions;

export const initialCartState: CartState = {
  cartDataReady: false,
  canBuyList: [],
  canBuyCartsList: [],
  pendingLensList: [],
  canNotBuyList: [],
  selectedCartIds: [],
  subTotal: {
    total: '0.00',
    discount: '0.00',
    code_discount: '0.00',
    point_discount: '0.00',
    subtotal: '0.00',
  },
  giftData: {
    should_pop: false,
    is_pick_up: false,
    cart: null,
  },
  giftCount: 0,
  uuidOrderList: [],
};

export const createCartSlice: StateCreator<Store, [], [], CartSlice> = (set, get) => ({
  ...initialCartState,

  // 基础设置方法
  setCanBuyList: (canBuyList) => {
    set({ canBuyList });
    setLocal('zl-canBuyList', canBuyList);
  },
  setCanBuyCartsList: (canBuyCartsList) => {
    set({ canBuyCartsList });
    setLocal('zl-canBuyCartsList', canBuyCartsList);
  },
  setPendingLensList: (pendingLensList) => {
    set({ pendingLensList });
  },
  setCanNotBuyList: (canNotBuyList) => {
    set({ canNotBuyList });
  },
  setSubTotal: (subTotal) =>
    set({
      subTotal: {
        ...subTotal,
        applied_offer: subTotal.applied_offer
          ? normalizeAppliedOffer(subTotal.applied_offer)
          : undefined,
      },
    }),
  setGiftData: (giftData) => set({ giftData }),
  setGiftCount: (giftCount) => set({ giftCount }),
  setUuidOrderList: (uuidOrderList: number[]) => {
    set({ uuidOrderList });
    setLocal('zl-uuidOrderList', uuidOrderList);
  },
  setSelectedCartIds: (selectedCartIds) => {
    set({ selectedCartIds });
    setLocal('zl-selectedCart', selectedCartIds);
  },
  // === Vue项目cart.js中对应的actions ===
  setCart: async (cartdata) => {
    set({ cartDataReady: false });
    const {
      setCanBuyList,
      setCanBuyCartsList,
      setPendingLensList,
      setCanNotBuyList,
      setGiftData,
      setGiftCount,
      cartTotalPrice,
    } = get() as CartSlice;

    let data: any = {};
    if (cartdata) {
      data = cartdata;
    } else {
      // 调用购物车列表接口
      const response = await getNewCartList({
        same_spu: typeof window !== 'undefined' && window.location.pathname === '/cart',
      });
      data = response.data;
    }

    // 处理购物车数据格式（对应Vue项目中的cartInforEdit函数）
    if (data.list) {
      data.list = data.list.map((ele: any) => {
        return {
          ...ele,
          carts: ele.carts.map((item: any) => {
            return {
              ...item,
              lenseShow: false,
            };
          }),
          selected: false,
          disabled: true, // 是否可以选中
        };
      });
    }

    if (data.can_not_sell_list) {
      data.can_not_sell_list = data.can_not_sell_list.map((ele: any) => {
        return {
          ...ele,
          lenseShow: false,
          selected: false,
        };
      });
    }

    // 更新store状态
    setGiftData(data.new_gift_popup || { should_pop: false, is_pick_up: false, cart: null });
    setGiftCount(data.total_gift_count || 0);
    setCanBuyList(data.list || []);
    setCanBuyCartsList(data.can_buy_list || []);
    setPendingLensList(data.pending_lens_list || []);
    setCanNotBuyList(data.can_not_sell_list || []);
    set({ cartDataReady: true });

    // 更新pc端用户顶栏购物车浮窗展示价格（此处为全部勾选时的价格）
    // 只有在PC端且不是购物车页面的时候才调用
    if (typeof window !== 'undefined') {
      const deviceType = getCookie('deviceType') || String(EPlatform.WAP); // 默认为WAP
      /**
       * todo:
       * 赵艺的电脑 windows，不知道为什么不会发起 cartTotalPrice 的请求，但是她那访问主站线上又是好的。
       * 本地始终无法复现，试试屏幕宽度替代 cookie 中的方式。
       * https://devops.aliyun.com/projex/project/ce85697916dd382a20b8106373/bug/a6c79d4ffbbb86c1c7f0088b0c
       */
      const isPC = window.innerWidth >= MOBILE_SCREEN_WIDTH || deviceType === String(EPlatform.WEB); // WEB端是1
      // const isCartPage = window.location.pathname.includes('/cart');

      if (isPC) {
        await cartTotalPrice();
      }
    }

    // Emarsys埋点
    if (typeof window !== 'undefined') {
      const center = ReportingCenter.getInstance();
      const emarsysProvider = center.getProvider(ProviderType.EMARSYS) as EmarsysProvider;
      const arr = [];
      data.can_buy_list.forEach((item) => {
        const cartData = item.carts?.[0];
        if (!cartData?.shop_sku) return;
        arr.push({
          item: cartData.shop_sku, // 购物车ID
          quantity: cartData.qty, // 数量
          price: parseFloat(genItemPrice(cartData, false, env.NEXT_PUBLIC_DEFAULT_CURRENCY)), // 以美元为基础的售价
        });
      });
      if (emarsysProvider) {
        // ProviderType.EMARSYS
        emarsysProvider.track({
          eventName: 'cart',
          properties: arr,
        });
      }
    }

    return {
      canBuyList: data.list || [],
      canBuyCartsList: data.can_buy_list || [],
      pendingLensList: data.pending_lens_list || [],
      canNotBuyList: data.can_not_sell_list || [],
    };
  },
  // 用于pc端购物车悬浮窗展示价格
  cartTotalPrice: async () => {
    const { canBuyCartsList, setSubTotal } = get();
    const store = get() as Store;
    const cartPopoverAutoOfferParams: { auto_apply_offer?: 1 } = {};
    let cartAutoOfferAbValue: number | null | undefined;

    if (IS_US) {
      cartAutoOfferAbValue = await fetchCartAutoOfferAbValue().catch((error) => {
        logger.error('Failed to get cart popover auto offer AB config:', error);
        return null;
      });
      if (cartAutoOfferAbValue === 2) {
        cartPopoverAutoOfferParams.auto_apply_offer = 1;
      }
    }

    // 获取可结算列表的cartids
    const arr: number[] = [];
    canBuyCartsList.forEach((item) => {
      item.carts.forEach((items) => {
        arr.push(items.cart_id);
      });
    });

    if (arr.length === 0) {
      return;
    }

    try {
      const response = await getSubTotalPrice({
        currency: store.currency?.name,
        cart_ids: arr,
        ...cartPopoverAutoOfferParams,
      });
      setSubTotal(response.data);
    } catch (error) {
      logger.error('Failed to get cart total price:', error);
    }
  },
});
